<script setup>

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

const statsClass = computed(() => {
  const cards = props.block?.statsContent?.[0]?.cards || []
  return cards.length === 3 ? 'stats--three' : ''
})

const headingText = (card) => {
  const html = card?.heading?.html
  if (!html) {
    return card?.heading?.plainText?.replaceAll('amp;', '') || ''
  }
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .trim()
}
</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="stats container section-mb">
  <div v-if="block.heading?.html"
    class="stats-heading"
    v-html="block.heading.html"></div>

  <div v-if="block.statsContent?.length" class="stats-content"  :class="statsClass">
    <div v-for="card in block.statsContent[0].cards"
        :key="card.id"
        class="stats-content__card" :class="{ '--full-cover': card.isFullCover }">
      <div class="stats-content__card-image">
        <GlobalsImage
          v-if="card.imageDesktop?.length || card.imageMobile?.length"
          :landscape="card.imageDesktop?.[0]"
          :portrait="card.imageMobile?.[0]"
          :max-width="1600"
          :ariaHidden="true"
        />
      </div>

      <div class="stats-content__card-content">
        <p class="stats-content__card-title">{{ headingText(card) }}</p>
        <p>
          {{ card.description }}
        </p>
      </div>
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.stats-heading {
  margin-bottom: 32px;
  
  @media (min-width: 1024px) {
    margin-bottom: 48px;
    text-align: center;
  }
}

.stats-content {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  flex-wrap: wrap;
}

.stats-content__card {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background-color: var(--color-gray-100);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  width: 100%;
}

.stats-content__card .stats-content__card-title {
  position: relative;
  z-index: 1;
  font-family: var(--font-family-primary);
  white-space: pre-line;
}

.stats-content__card p {
  font-size: 16px;
  line-height: 100%;
  letter-spacing: -0.32px;
  color: var(--color-gray-500);
  position: relative;
  z-index: 1;
}

.stats-content__card:nth-child(1),
.stats-content__card:nth-child(4),
.stats-content__card:nth-child(5) {
  height: calc(152 / 390 * 100vw);
}

.stats-content__card:nth-child(2) {
  height: calc(326 / 390 * 100vw);
}

.stats-content__card:nth-child(3) {
  height: calc(506 / 390 * 100vw);
}

.stats-content__card:nth-child(6) {
  height: calc(332 / 390 * 100vw);
}

.stats-content__card:nth-child(4),
.stats-content__card:nth-child(5) {
  width: calc(50% - 8px);
}

.stats--three .stats-content__card {
  padding: 24px 29px 37px 24px;
  margin-bottom: 16px;
}

.stats--three .stats-content__card:nth-child(1) {
  min-height: 428px;
  background: unset;
  height: unset;
}

.stats--three .stats-content__card:nth-child(2) {
  min-height: 289px;
  height: auto;
  height: initial;
}

.stats--three .stats-content__card:nth-child(3) {
min-height: 345px;
padding-bottom: 31px;
height: unset;
}

.stats--three .stats-content__card:nth-child(1) .stats-content__card-image img {
  object-fit: cover;
}

.stats--three .stats-content__card:nth-child(2) .stats-content__card-image :deep(img),
.stats--three .stats-content__card:nth-child(3) .stats-content__card-image :deep(img) {
  object-fit: contain;
}

.stats--three .stats-content__card:nth-child(2) .stats-content__card-image {
  margin-top: 36px;
    margin-inline: auto;
    max-width: 384px;
    height: 42px;
    position: relative;
    order: 3;
}

.stats--three .stats-content__card:nth-child(3) .stats-content__card-image {
  margin-top: 36px;
  margin-inline: auto;
  max-width: 384px;
  width: 100%;
  height: auto;
  position: relative;
  order: 3;
}

.stats--three .stats-content__card:nth-child(3) .stats-content__card-image :deep(img) {
  width: 100%;
  height: auto;
}

.stats-content__card:nth-child(1) .stats-content__card-title {
  font-size: 40px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: -1.5px;
  color: var(--color-black);
  margin-bottom: 20px;
}

.stats-content__card:nth-child(2) .stats-content__card-title,
.stats-content__card:nth-child(3) .stats-content__card-title,
.stats-content__card:nth-child(6) .stats-content__card-title {
  font-size: 24px;
  font-weight: 500;
  line-height: 110%;
  letter-spacing: -0.48px;
  font-family: var(--font-family-primary);
  color: var(--color-black-100);
}

.stats-content__card:nth-child(4) .stats-content__card-title,
.stats-content__card:nth-child(5) .stats-content__card-title {
  font-size: 30px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: -1.5px;
  color: var(--color-black);
  margin-bottom: 16px;
}

.stats--three.stats-content .stats-content__card .stats-content__card-title {
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 500;
  line-height: 144%; /* 28.8px */
  letter-spacing: -0.4px;
  color: #0d0d0d;
  margin-bottom: 12px;
}

.stats--three .stats-content__card p {
  color: var(--color-gray-500);
  font-size: 14px;
  line-height: 140%;
  letter-spacing: -0.28px;
}

.stats-content__card-image {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.stats-content__card-image img {
  height: 100%;
  object-fit: cover;
  object-position: center;
}


@media (min-width: 1024px) {

  .stats-content {
    gap: 16px;
    display: grid !important;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, max-content);
  }

  .stats-content.stats--three {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: unset;
  }

  .stats-content__card {
    margin-bottom: 0;
  }

  .stats--three .stats-content__card {
    margin: 0;
    max-height: unset;
    padding: 40px 32px;
  }

  .stats-content__card:nth-child(1),
  .stats-content__card:nth-child(4),
  .stats-content__card:nth-child(5) {
    height: calc((100vw - 64px) / 4 * (184 / 376));
    max-height: 184px;
  }

  .stats--three .stats-content__card:nth-child(1) {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
    max-height: unset;
    height: unset;
  }

  .stats--three .stats-content__card:nth-child(2) {
            grid-column: 2 / 3;
        grid-row: 1 / 2;
        max-height: unset;
    height: unset;
  }

    .stats--three .stats-content__card:nth-child(3) {
              grid-column: 2 / 3;
        grid-row: 2 / 3;
        max-height: unset;
    height: unset;
    }

  .stats-content__card:nth-child(2),
  .stats-content__card:nth-child(6) {
    height: calc((100vw - 64px) / 4 * (406 / 376));
    max-height: 406px;
  }

  .stats-content__card:nth-child(3) {
    height: 100%;
  }

  .stats-content__card:nth-child(1) {
    grid-column: 1 / span 1;
    grid-row: 1 / span 1;
  }

  .stats-content__card:nth-child(2) {
    grid-column: 1 / span 1;
    grid-row: 2 / span 1;
  }

  .stats-content__card:nth-child(3) {
    grid-column: 2 / span 1;
    grid-row: 1 / span 2;
  }

  .stats-content__card:nth-child(4),
  .stats-content__card:nth-child(5) {
    width: unset;
  }

  .stats-content__card:nth-child(6) {
    grid-column: 3 / span 2;
    grid-row: 2 / span 1;
  }

  .stats-content__card:nth-child(6) .stats-content__card-title {
    max-width: 60%;
  }

   .stats--three.stats-content .stats-content__card .stats-content__card-title {
    font-size: 28px !important;
    line-height: 132% !important; /* 36.96px */
    letter-spacing: -0.56px !important;
    color: var(--color-gray-800) !important;
   }

    .stats--three .stats-content__card p {
      font-size: 16px !important;
      line-height: 140% !important;
      letter-spacing: -0.32px;
    }

    .stats--three .stats-content__card:nth-child(2) .stats-content__card-image {
      height: 72px;
      margin-top: 42px;
      max-width: unset;
    }

    .stats--three .stats-content__card:nth-child(3) .stats-content__card-image {
      height: 136px;
      margin-top: 68px;
      max-width: unset;
    }

    .stats--three .stats-content__card:nth-child(2) .stats-content__card-image :deep(img),
    .stats--three .stats-content__card:nth-child(3) .stats-content__card-image :deep(img) {
      height: 100%;
      width: auto;
      max-height: unset;
      margin-inline: auto;
    }

  .stats-content:not(.stats--three) .stats-content__card:nth-child(1) .stats-content__card-title {
    font-size: 30px;
    margin-bottom: 16px;
  }

  .stats-content:not(.stats--three) .stats-content__card:nth-child(2) .stats-content__card-title,
  .stats-content:not(.stats--three) .stats-content__card:nth-child(3) .stats-content__card-title,
  .stats-content:not(.stats--three) .stats-content__card:nth-child(6) .stats-content__card-title {
    font-size: 18px;
  }

}

@media (min-width: 1440px) {

  .stats-content:not(.stats--three) .stats-content__card:nth-child(2) .stats-content__card-title,
  .stats-content:not(.stats--three) .stats-content__card:nth-child(3) .stats-content__card-title,
  .stats-content:not(.stats--three) .stats-content__card:nth-child(6) .stats-content__card-title {
    font-size: 28px;
    letter-spacing: -0.2px;
  }

  .stats-content:not(.stats--three) .stats-content__card:nth-child(1) .stats-content__card-title,
  .stats-content__card:nth-child(4) .stats-content__card-title,
  .stats-content__card:nth-child(5) .stats-content__card-title {
    font-size: 56px;
  }

  .stats-content__card p {
    font-size: 20px;
    letter-spacing: -0.4px;
  }

  .stats-content__card:nth-child(6) .stats-content__card-title {
    max-width: 400px;
  }

}

@media (max-width: 1023px) {
  
  .stats--three .stats-content__card:nth-child(2) {
    min-height: 373px !important;
  }

  .stats--three .stats-content__card:nth-child(2) :deep(img) {
      height: 172px !important;
  }
}

.--full-cover.stats-content__card {
  background-color: transparent !important;
  padding: 0 !important;
}

.--full-cover.stats-content__card .stats-content__card-image {
  position: relative !important;
  margin: 0 !important;
  height: 100% !important;
  max-width: 100% !important;
}

.--full-cover.stats-content__card .stats-content__card-content {
  position: absolute;
  z-index: 1;
  padding: 24px;

  @media (min-width: 1024px) {
    padding: 32px;
  }
}

.--full-cover.stats-content__card .stats-content__card-image :deep(img) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  object-position: center !important;
}

.stats--three .stats-content__card.--full-cover:nth-child(1),
.stats--three .stats-content__card.--full-cover:nth-child(2),
.stats--three .stats-content__card.--full-cover:nth-child(3) {
  min-height: unset !important;
}

</style>